import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

/**
 * AptSync / Repli360 "View Details" destination. The listing widget
 * (`rrac_script` on /floor-plans) links here; this page only mounts the
 * details shortcode so RRAC can fill in the selected plan.
 *
 * Script loading is delegated to the site-wide apartment-sync-loader
 * (already a global injection). Mounting it here as well keeps the
 * page self-contained if the loader is later removed, and `reinit`
 * asks RRAC to bind to this container after SPA navigation.
 */

interface ApartmentSyncGlobal {
  reinit?: () => void;
}

function reinitApartmentSync(): void {
  const api = (window as unknown as { __apartment_sync__?: ApartmentSyncGlobal })
    .__apartment_sync__;
  api?.reinit?.();
}

export function ApartmentSyncFloorplanDetails() {
  const blockSlot = useBlockSlot('apartmentsyncfloorplandetails.block', {
    defaultValue: 'apartment-sync-loader',
  });

  useEffect(() => {
    reinitApartmentSync();
  }, []);

  return (
    <section className="apartmentsync-floorplan-details bg-background text-foreground">
      {blockSlot ? (
        <SectionContextProvider sectionId={blockSlot.sectionId}>
          <blockSlot.BlockComponent sectionId={blockSlot.sectionId} />
        </SectionContextProvider>
      ) : null}

      <EditableToken
        path="apartmentsyncfloorplandetails.block"
        as="div"
        className="apartmentsync-floorplan-details__inner"
      >
        <div className="rrac_floorplanDetails" />
      </EditableToken>

      <style>{`
        .apartmentsync-floorplan-details {
          width: 100%;
          min-height: 400px;
        }
        .apartmentsync-floorplan-details__inner {
          min-height: 400px;
          background: #ffffff;
          color: #333333;
        }
        .apartmentsync-floorplan-details .rrac_floorplanDetails {
          position: relative;
          overflow: hidden;
          min-height: 400px;
          background: #ffffff;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ApartmentSyncFloorplanDetails',
  displayName: 'ApartmentSync Floor Plan Details',
  tokens: {
    'apartmentsyncfloorplandetails.block': {
      label: 'Block',
      type: 'block',
      defaultValue: 'apartment-sync-loader',
      acceptedBlockCategories: ['Integrations'],
      description:
        'Loads the ApartmentSync (Repli360) script so the rrac_floorplanDetails shortcode can render.',
    },
  },
};

(ApartmentSyncFloorplanDetails as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ApartmentSyncFloorplanDetails;
